import { useRef, useCallback, useEffect } from 'react';
import { alpha, Box } from '@mui/material';
import { getCrmPalette } from '../../../theme/brand';

/**
 * Avatar de Gustavo ASOMÁNDOSE de una BURBUJA DORADA (Fase 3, paso 1): usa el
 * recorte transparente (public/gustavo-cutout.png). Una máscara revela el cuerpo
 * DENTRO del círculo dorado y deja la CABEZA sobresalir por arriba → efecto 3D de
 * "asomarse". Es CALMO: flota apenas y reacciona al mouse/click, pero NO se mueve
 * solo a los costados. Hace un gesto suave al clickearlo o cuando aparece una
 * sugerencia; la señal de sugerencia es el halo dorado que se aviva. Los gestos
 * animan al personaje entero (brazos/boca articulados requieren un rig tipo Rive).
 */

const CUTOUT = '/gustavo-cutout.png';

// Máscara: disco del círculo (cuerpo adentro) ∪ elipse de la cabeza (asoma arriba).
// Coordenadas en el espacio del wrapper (60×78; círculo centrado en 30,48 r28).
const MASK = 'radial-gradient(circle 28px at 30px 48px, #000 27px, transparent 28px), radial-gradient(12px 25px at 30px 11px, #000 60%, transparent 100%)';

// Gestos one-shot SOLO verticales/suaves (sin movimiento lateral que parezca vibración).
const GESTURE_CSS = `
@keyframes gvfab-hop   { 0%,100%{transform:translateY(0) scaleY(1)} 20%{transform:translateY(1px) scaleY(.97)} 50%{transform:translateY(-5px) scaleY(1.03)} 80%{transform:translateY(0) scaleY(.99)} }
@keyframes gvfab-greet { 0%{transform:translateY(0) scale(1)} 30%{transform:translateY(-6px) scale(1.04)} 60%{transform:translateY(0) scale(.99)} 100%{transform:translateY(0) scale(1)} }
.gvfab-hop   { animation: gvfab-hop   .7s cubic-bezier(.3,.7,.3,1); }
.gvfab-greet { animation: gvfab-greet .8s cubic-bezier(.3,.7,.3,1); }
`;

export function GustavoAvatarFab({ onClick, hint = false, notices = 0, noticePreview }: {
  onClick: () => void;
  hint?: boolean;
  /** Avisos de Gustavo sin leer (correcciones de soporte, re-contactos). */
  notices?: number;
  /** Primeras palabras del último aviso, para el tooltip. */
  noticePreview?: string | null;
}) {
  /**
   * El aura sigue a la MARCA, no al oro.
   *
   * Estos colores estaban escritos a mano (`#D8BF82`, `rgba(216,191,130,·)`): el dorado de
   * Imperium metido en un componente que también corre en Booster y en cada white-label.
   * Ahora salen de la paleta activa, así que en Imperium se ve igual que siempre y en el
   * resto toma el color de su marca. (20/08/2026)
   */
  const MARCA = getCrmPalette().brand;
  const rgbaMarca = (a: number) => alpha(MARCA.golden, a);

  const gestureRef = useRef<HTMLDivElement>(null);
  const bodyRef     = useRef<HTMLDivElement>(null);   // capa de tilt 3D (leve, solo al mouse)
  const imgRef      = useRef<HTMLImageElement>(null); // parallax sutil
  const hoveringRef = useRef(false);

  // ── seguimiento del mouse (suave, solo mientras pasás por encima) ──
  const onMove = useCallback((e: React.PointerEvent) => {
    hoveringRef.current = true;
    const el = bodyRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = Math.max(-1, Math.min(1, ((e.clientX - (r.left + r.width / 2)) / r.width) * 2));
    const py = Math.max(-1, Math.min(1, ((e.clientY - (r.top + r.height / 2)) / r.height) * 2));
    el.style.transition = 'transform .12s cubic-bezier(.2,.7,.2,1)';
    el.style.transform = `rotateX(${-py * 7}deg) rotateY(${px * 10}deg)`;
    if (imgRef.current) imgRef.current.style.transform = `translate(${px * 2}px, ${py * 1.5}px)`;
  }, []);

  const onLeave = useCallback(() => {
    hoveringRef.current = false;
    const el = bodyRef.current;
    if (el) { el.style.transition = 'transform .5s cubic-bezier(.2,.7,.2,1)'; el.style.transform = ''; }
    if (imgRef.current) imgRef.current.style.transform = '';
  }, []);

  // ── gestos (verticales/suaves) ──
  const playGesture = useCallback((cls: string) => {
    const el = gestureRef.current;
    if (!el) return;
    el.classList.remove('gvfab-hop', 'gvfab-greet');
    void el.offsetWidth; // reflow → re-dispara la animación
    el.classList.add(cls);
  }, []);

  // Reacciona cuando aparece una sugerencia contextual (burbuja).
  useEffect(() => {
    const onNudge = () => playGesture('gvfab-greet');
    window.addEventListener('gustavo:nudge', onNudge);
    return () => window.removeEventListener('gustavo:nudge', onNudge);
  }, [playGesture]);

  // ── loop autónomo CALMO: solo un saltito suave muy de vez en cuando ──
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const tick = () => {
      const delay = 8000 + Math.random() * 7000; // 8–15s (poco frecuente)
      t = setTimeout(() => {
        if (!hoveringRef.current) playGesture('gvfab-hop');
        tick();
      }, delay);
    };
    tick();
    return () => clearTimeout(t);
  }, [playGesture]);

  // Aviso nuevo → saludo, para que el puntito no aparezca en silencio.
  const prevNotices = useRef(notices);
  useEffect(() => {
    if (notices > prevNotices.current) playGesture('gvfab-greet');
    prevNotices.current = notices;
  }, [notices, playGesture]);

  return (
    <Box
      role="button"
      aria-label={notices > 0 ? `Abrir Gustavo — ${notices} aviso${notices > 1 ? 's' : ''} sin leer` : 'Abrir Gustavo'}
      title={notices > 0 ? (noticePreview || `Tenés ${notices} aviso${notices > 1 ? 's' : ''} de Gustavo sin leer`) : undefined}
      onClick={() => { playGesture('gvfab-greet'); onClick(); }}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      sx={{
        position: 'relative',
        width: 60, height: 78, cursor: 'pointer', perspective: '420px',
        animation: 'gvfab-float 6s ease-in-out infinite',
        '@keyframes gvfab-float': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-4px)' } },
        '@keyframes gvfab-glow':  { '0%,100%': { opacity: 0.55, transform: 'scale(1)' }, '50%': { opacity: 0.85, transform: 'scale(1.06)' } },
        '@keyframes gvfab-glowHi':{ '0%,100%': { opacity: 0.8, transform: 'scale(1.02)' }, '50%': { opacity: 1, transform: 'scale(1.12)' } },
        '@keyframes gvfab-spin':  { to: { transform: 'rotate(360deg)' } },
        '&:hover .gvfab-bub': { borderColor: MARCA.goldenLight },
      }}
    >
      <style>{GESTURE_CSS}</style>

      {/* halo dorado detrás de la burbuja (pulsa; se aviva con sugerencia) */}
      <Box sx={{
        position: 'absolute', left: 0, bottom: 0, width: 60, height: 60, borderRadius: '50%',
        pointerEvents: 'none', filter: 'blur(6px)',
        background: `radial-gradient(circle, ${rgbaMarca(hint ? 0.5 : 0.34)}, ${rgbaMarca(0.06)} 48%, transparent 70%)`,
        animation: `${hint ? 'gvfab-glowHi 2.2s' : 'gvfab-glow 3.6s'} ease-in-out infinite`,
      }} />

      {/* burbuja dorada — círculo con borde e interior oscuro (detrás del personaje) */}
      <Box className="gvfab-bub" sx={{
        position: 'absolute', left: 0, bottom: 0, width: 60, height: 60, borderRadius: '50%',
        border: `2px solid ${hint ? MARCA.goldenLight : MARCA.golden}`,
        background: 'radial-gradient(circle at 50% 40%, #201c15, #0e0d0b 80%)',
        boxShadow: `0 8px 20px rgba(0,0,0,.5), 0 0 16px ${rgbaMarca(0.28)}, inset 0 1px 2px ${rgbaMarca(0.2)}`,
        transition: 'border-color .3s',
      }} />

      {/* luz que VIAJA sobre la línea del círculo (segmentos girando) → llama la atención */}
      <Box sx={{
        position: 'absolute', left: -2, bottom: -2, width: 64, height: 64, borderRadius: '50%', pointerEvents: 'none',
        background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(MARCA.goldenLight, hint ? 1 : 0.85)} 22deg, transparent 55deg, transparent 180deg, ${rgbaMarca(hint ? 0.9 : 0.7)} 202deg, transparent 235deg)`,
        animation: `gvfab-spin ${hint ? 2.4 : 3.6}s linear infinite`,
        WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
        mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
      }} />

      {/* personaje enmascarado: cuerpo dentro del círculo, cabeza asomando arriba */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        WebkitMaskImage: MASK, maskImage: MASK,
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
      }}>
        {/* capa de GESTO (saltito/greet, vertical) */}
        <Box ref={gestureRef} sx={{ position: 'absolute', inset: 0 }}>
          {/* capa de TILT (leve, al mouse) */}
          <Box ref={bodyRef} sx={{
            position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transformOrigin: 'center bottom',
          }}>
            <Box
              component="img" ref={imgRef} src={CUTOUT} alt="Gustavo" draggable={false}
              sx={{
                position: 'absolute', left: '50%', bottom: 0, marginLeft: '-22px', // 44/2 → centrado; transform libre para el parallax
                width: 44, height: 'auto', transformOrigin: 'center bottom', userSelect: 'none',
                transition: 'transform .18s cubic-bezier(.2,.7,.2,1)',
                filter: 'drop-shadow(0 3px 5px rgba(0,0,0,.5))',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Avisos sin leer: puntito rojo arriba a la derecha, como WhatsApp. Va FUERA
          de la máscara del personaje (si no, lo recorta) y encima de todo. Sin esto
          los avisos de Gustavo —las correcciones que le mandamos cuando algo salió
          mal— quedaban en el chat sin que nada los señalara. */}
      {notices > 0 && (
        <Box sx={{
          position: 'absolute', top: 0, right: -2, zIndex: 3, pointerEvents: 'none',
          minWidth: 20, height: 20, px: notices > 9 ? 0.6 : 0,
          borderRadius: '10px',
          bgcolor: '#EF4444', border: '2px solid #0e0d0b',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(239,68,68,.55)',
          animation: 'gvfab-pop .35s cubic-bezier(.2,1.4,.4,1)',
          '@keyframes gvfab-pop': {
            '0%':   { transform: 'scale(0)' },
            '70%':  { transform: 'scale(1.15)' },
            '100%': { transform: 'scale(1)' },
          },
        }}>
          <Box component="span" sx={{
            fontSize: 11, fontWeight: 800, color: '#fff', lineHeight: 1,
            fontFamily: 'system-ui, sans-serif',
          }}>
            {notices > 9 ? '9+' : notices}
          </Box>
        </Box>
      )}
    </Box>
  );
}
